import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { t } from '../i18n';

/**
 * L'appel tenu par l'application, et non par la vue web.
 *
 * Le releve fait sur l'iPhone a tranche : pendant un appel, la session
 * portait exactement ce qu'on lui demandait — `PlayAndRecord / VoiceChat`
 * sans `defaultToSpeaker`, dont la sortie par defaut est l'ecouteur — et
 * la route restait sur le haut-parleur. Ce n'etait donc pas la categorie
 * qui nous echappait, c'est la route, reposee par-dessus la notre en
 * boucle. Le bouton s'eteignait une seconde puis se rallumait : la course
 * etait visible, et perdue.
 *
 * On ne gagne pas contre le proprietaire de la session. Tant que
 * `getUserMedia` tourne dans la webview, ce proprietaire est WebKit. On
 * lui retire donc l'appel : `AppelNatif.swift` porte la connexion, le
 * micro et la voix de l'autre, et plus rien dans la page ne demande de
 * capture.
 *
 * Ce module est la couture. Il presente au reste du code la meme surface
 * qu'une `RTCPeerConnection`, si bien que `calls.ts` garde sa
 * signalisation, ses ecoutes et son enchainement — ce qui marchait n'a
 * pas eu a etre reecrit.
 */

/** Un emplacement mesure dans la page, ou le natif viendra dessiner. */
export interface Cadre {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Description {
  type: string;
  sdp: string;
}

interface CandidatNatif {
  candidate: string;
  sdpMid: string;
  sdpMLineIndex: number;
}

interface AppelNatifPlugin {
  disponible(): Promise<{ oui: boolean }>;
  ouvrir(options: {
    hautParleur: boolean;
    iceServers: Array<{ urls: string[] }>;
  }): Promise<void>;
  demarrerVideo(): Promise<void>;
  couperCamera(options: { coupe: boolean }): Promise<{ coupe: boolean }>;
  retournerCamera(): Promise<{ avant: boolean }>;
  placerVues(options: {
    grande: Cadre;
    petite: Cadre;
    moiEnGrand: boolean;
  }): Promise<void>;
  offrir(): Promise<Description>;
  repondre(options: { sdp: string }): Promise<Description>;
  poserDistant(options: { type: string; sdp: string }): Promise<void>;
  ajouterCandidat(options: {
    candidate: string;
    sdpMid: string;
    sdpMLineIndex: number;
  }): Promise<void>;
  muter(options: { coupe: boolean }): Promise<{ coupe: boolean }>;
  hautParleur(options: { actif: boolean }): Promise<{ hautParleur: boolean; route: string }>;
  sonnerie(options: { actif: boolean }): Promise<void>;
  raccrocher(): Promise<void>;
  addListener(
    event: 'candidat',
    cb: (c: CandidatNatif) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'etat',
    cb: (e: { etat: 'connecte' | 'echec' | 'ferme' }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'son',
    cb: (e: { hautParleur: boolean; route: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const greffon = registerPlugin<AppelNatifPlugin>('AppelNatif');

/**
 * Sur l'iPhone, tous les appels passent par l'application.
 *
 * La video y est venue apres la voix, et pour la meme raison : la sortie
 * du son ne s'y commandait pas davantage, puisque c'est la camera ouverte
 * dans la vue web qui donnait la session a WebKit.
 */
export function appelNatifDisponible(): boolean {
  /*
    iOS seulement, et c'est voulu : ce greffon existe parce que WebKit
    possede la session audio et repose la route en boucle. La WebView
    d'Android est Chromium — `getUserMedia` y route correctement, donc
    l'appel reste dans la page, comme sur le web.
  */
  return Capacitor.getPlatform() === 'ios';
}

/*
  Un appel natif est-il en cours ?

  Le bouton du haut-parleur passe par `hautParleur.ts`, qui n'a aucun
  moyen de savoir quel chemin porte l'appel du moment. Un drapeau de
  module suffit : il n'y a jamais qu'un appel a la fois — l'ecran s'en
  assure par `occupe` — et le faire voyager jusqu'ici aurait traverse
  quatre signatures pour une seule information.
*/
let enCours = false;

export function appelNatifEnCours(): boolean {
  return enCours;
}

/**
 * La tonalite d'attente, jouee par l'application.
 *
 * Elle ne peut pas venir de la page : une balise qui joue rend la session
 * audio a WebKit, et la route de l'appel avec — c'est exactement ce que
 * ce module existe pour eviter.
 */
export async function sonnerieNative(actif: boolean): Promise<void> {
  await greffon.sonnerie({ actif }).catch(() => undefined);
}

/** La sortie du son d'un appel natif. */
export async function hautParleurNatif(
  actif: boolean,
): Promise<{ obtenu: boolean; route: string }> {
  const r = await greffon.hautParleur({ actif });
  return { obtenu: Boolean(r?.hautParleur), route: r?.route ?? '?' };
}

/**
 * Une connexion pair-a-pair portee par l'application.
 *
 * Elle expose ce que `calls.ts` attend d'une `RTCPeerConnection`, et rien
 * de plus. Deux differences d'enchainement, assumees et sans consequence
 * pour l'appelant :
 *
 *  - le natif pose lui-meme la description locale en la fabriquant, donc
 *    `setLocalDescription` n'a plus rien a faire ;
 *  - une reponse se fabrique a partir de l'offre distante, que le natif
 *    veut d'un seul tenant : on retient donc l'offre a
 *    `setRemoteDescription` et on la remet a `createAnswer`.
 *
 * Le reste — candidats, etat du lien, fermeture — se transpose un pour un.
 */
export class ConnexionNative {
  onicecandidate: ((e: { candidate: RTCIceCandidate | null }) => void) | null = null;
  onconnectionstatechange: (() => void) | null = null;
  ontrack: ((e: RTCTrackEvent) => void) | null = null;

  connectionState: RTCPeerConnectionState = 'new';
  signalingState: RTCSignalingState = 'stable';
  currentRemoteDescription: RTCSessionDescription | null = null;

  private offreEnAttente: string | null = null;
  private abonnements: Array<Promise<PluginListenerHandle>> = [];

  /**
   * Ouvre la connexion cote natif.
   *
   * A appeler avant tout le reste : le constructeur ne peut pas attendre,
   * et la permission du micro se demande la-bas.
   */
  async ouvrir(
    serveurs: RTCConfiguration,
    hautParleurAuDepart: boolean,
    avecVideo: boolean,
  ): Promise<void> {
    const iceServers = (serveurs.iceServers ?? []).map((s) => ({
      urls: Array.isArray(s.urls) ? s.urls : [s.urls],
    }));

    this.abonnements.push(
      greffon.addListener('candidat', (c) => {
        /*
          Un vrai `RTCIceCandidate`, et non l'objet nu qu'il contient.

          `calls.ts` depose les candidats par `candidate.toJSON()` — ce que
          fait le navigateur, et ce qu'un objet fabrique a la main n'a pas.
          Le gestionnaire levait donc a chaque chemin decouvert, aucun
          n'etait publie, et l'appel restait indefiniment en « Connexion »
          alors que la signalisation, elle, avait abouti. Construire la
          vraie classe ne coute rien : elle n'ouvre aucune capture.
        */
        this.onicecandidate?.({
          candidate: new RTCIceCandidate({
            candidate: c.candidate,
            sdpMid: c.sdpMid || null,
            sdpMLineIndex: c.sdpMLineIndex,
          }),
        });
      }),
      greffon.addListener('etat', ({ etat }) => {
        this.connectionState =
          etat === 'connecte' ? 'connected' : etat === 'echec' ? 'failed' : 'closed';
        this.onconnectionstatechange?.();
      }),
    );

    await greffon.ouvrir({ hautParleur: hautParleurAuDepart, iceServers });
    if (avecVideo) await greffon.demarrerVideo();
    enCours = true;
  }

  /** Ouvre la camera en cours d'appel, pour la montee en visio. */
  async demarrerVideo(): Promise<void> {
    await greffon.demarrerVideo();
  }

  /** Coupe sa propre camera. Rend l'etat obtenu. */
  async couperCamera(coupe: boolean): Promise<boolean> {
    const r = await greffon.couperCamera({ coupe });
    return Boolean(r?.coupe);
  }

  /**
   * Passe a l'autre camera. Rend `true` si c'est celle de l'ecran.
   *
   * La methode n'existe que dans les coquilles qui la portent : une plus
   * ancienne, qui recoit quand meme cette interface, rejette l'appel, et
   * le geste ne fait simplement rien. C'est pourquoi `NATIF_MINIMUM` ne
   * monte pas pour elle — il priverait toutes les coquilles d'avant de
   * chaque mise a jour a venir, pour un geste que rien n'annonce.
   */
  async retournerCamera(): Promise<boolean> {
    const r = await greffon.retournerCamera();
    return r.avant;
  }

  /** Le micro est ouvert cote natif : il n'y a pas de piste a poser ici. */
  addTrack(): void {}

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const offre = await greffon.offrir();
    this.signalingState = 'have-local-offer';
    return { type: 'offer', sdp: offre.sdp };
  }

  /** Deja posee par le natif au moment de la fabriquer. */
  async setLocalDescription(): Promise<void> {}

  async createAnswer(): Promise<RTCSessionDescriptionInit> {
    if (!this.offreEnAttente) throw new Error(t.appels.natif.pasAbouti);
    const reponse = await greffon.repondre({ sdp: this.offreEnAttente });
    this.offreEnAttente = null;
    this.signalingState = 'stable';
    return { type: 'answer', sdp: reponse.sdp };
  }

  async setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void> {
    if (description.type === 'offer') {
      // Retenue : le natif veut l'offre et la reponse d'un seul tenant.
      this.offreEnAttente = description.sdp ?? '';
      this.currentRemoteDescription = description as RTCSessionDescription;
      return;
    }
    await greffon.poserDistant({ type: 'answer', sdp: description.sdp ?? '' });
    this.currentRemoteDescription = description as RTCSessionDescription;
    this.signalingState = 'stable';
  }

  async addIceCandidate(candidat: RTCIceCandidateInit): Promise<void> {
    await greffon.ajouterCandidat({
      candidate: candidat.candidate ?? '',
      sdpMid: candidat.sdpMid ?? '',
      sdpMLineIndex: candidat.sdpMLineIndex ?? 0,
    });
  }

  /** Coupe le micro sans le fermer. Rend l'etat obtenu. */
  async muter(coupe: boolean): Promise<boolean> {
    const r = await greffon.muter({ coupe });
    return Boolean(r?.coupe);
  }

  close(): void {
    enCours = false;
    this.connectionState = 'closed';
    for (const abonnement of this.abonnements) {
      void abonnement.then((h) => h.remove()).catch(() => undefined);
    }
    this.abonnements.length = 0;
    void greffon.raccrocher().catch(() => undefined);
  }
}

/**
 * Dit au natif ou dessiner les deux images.
 *
 * La mise en page reste dans la feuille de style — elle seule sait ce que
 * vaut une encoche — et le natif s'y conforme. C'est aussi ce qui rend
 * l'echange gratuit : permuter les deux rectangles suffit.
 */
export async function placerLesVues(
  grande: Cadre,
  petite: Cadre,
  moiEnGrand: boolean,
): Promise<void> {
  await greffon.placerVues({ grande, petite, moiEnGrand }).catch(() => undefined);
}

/** Les changements de route pendant un appel natif. */
export function suivreLeSonNatif(
  ecoute: (etat: { hautParleur: boolean; route: string }) => void,
): Promise<PluginListenerHandle> {
  return greffon.addListener('son', ecoute);
}
